import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import HttpCotinService from '@/services/HttpCotinService';
import { useAuth } from '@/core/hooks/useAuth';
import { SidebarItem, SidebarShortcutItem } from './sidebar.types';
import {
    flattenSidebarItems,
    mapArvoreMenuToTree,
    omitAuditoriaItems,
    readArvoreMenuFromUsuario,
} from './sidebar.mapper';
import { Usuario } from '@/modules/auth/models/usuario.model';
import {
    getPreferenciasSnapshot,
    PREFERENCIA_ATALHOS_FAVORITOS,
    PREFERENCIA_AUDITORIA,
    Preferencia,
    subscribePreferencias,
} from '@/services/preferencias.service';

interface SidebarContextType {
    isCollapsed: boolean;
    isMobileOpen: boolean;
    menuItems: SidebarItem[];
    availableShortcuts: SidebarShortcutItem[];
    favoriteShortcutIds: string[];
    toggleCollapse: () => void;
    expandSidebar: () => void;
    collapseSidebar: () => void;
    toggleMobileSidebar: () => void;
    openMobileSidebar: () => void;
    closeMobileSidebar: () => void;
    isShortcutFavorite: (item: SidebarShortcutItem) => boolean;
    toggleFavoriteShortcut: (item: SidebarShortcutItem) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const PREFERENCIAS_API_URI = 'BFFProxy/configuracoesdeusuarios';

function uniqueValidIds(ids: unknown[], validIds: string[]): string[] {
    const valid = new Set(validIds);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const id of ids) {
        if (typeof id === 'string' && valid.has(id) && !seen.has(id)) {
            seen.add(id);
            result.push(id);
        }
    }
    return result;
}

function extractFavoriteIds(
    preferencias: Preferencia,
    usuario: Usuario | null,
    validIds: string[]
): string[] {
    const atalhosArvore =
        preferencias[PREFERENCIA_ATALHOS_FAVORITOS] ?? usuario?.atalhosRapidosArvoreMenu ?? [];
    const ids = Array.isArray(atalhosArvore) ? atalhosArvore : [];
    return uniqueValidIds(ids, validIds);
}

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { usuarioSessao } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [menuTree, setMenuTree] = useState<SidebarItem[]>([]);
    const [favoriteShortcutIds, setFavoriteShortcutIds] = useState<string[]>([]);
    const [auditoriaHabilitada, setAuditoriaHabilitada] = useState(() => {
        const preferencias = getPreferenciasSnapshot();
        return preferencias[PREFERENCIA_AUDITORIA] !== false;
    });

    useEffect(
        () =>
            subscribePreferencias((preferencias) => {
                setAuditoriaHabilitada(preferencias[PREFERENCIA_AUDITORIA] !== false);
            }),
        []
    );

    const menuItems = useMemo(
        () => (auditoriaHabilitada ? menuTree : omitAuditoriaItems(menuTree)),
        [auditoriaHabilitada, menuTree]
    );

    const availableShortcuts = useMemo(() => flattenSidebarItems(menuItems), [menuItems]);

    const routableIds = useMemo(
        () => availableShortcuts.filter((item) => !!item.route).map((item) => item.id),
        [availableShortcuts]
    );

    const persistFavoriteShortcuts = useCallback(
        async (favorites: string[]) => {
            const loginAD = usuarioSessao?.loginAD?.trim();
            if (!loginAD) {
                return;
            }
            try {
                await HttpCotinService.Put<void>(
                    `${PREFERENCIAS_API_URI}/${PREFERENCIA_ATALHOS_FAVORITOS}?loginAD=${encodeURIComponent(loginAD)}`,
                    favorites
                );
            } catch (error) {
                console.warn('Não foi possível salvar atalhos favoritos.', error);
            }
        },
        [usuarioSessao?.loginAD]
    );

    useEffect(() => {
        if (!usuarioSessao) {
            setMenuTree([]);
            setFavoriteShortcutIds([]);
            return;
        }

        setMenuTree(mapArvoreMenuToTree(readArvoreMenuFromUsuario(usuarioSessao)));
    }, [usuarioSessao]);

    useEffect(() => {
        if (!usuarioSessao) {
            setFavoriteShortcutIds([]);
            return;
        }

        const applyFavorites = (preferencias: Preferencia) => {
            setFavoriteShortcutIds(extractFavoriteIds(preferencias, usuarioSessao, routableIds));
        };

        applyFavorites(getPreferenciasSnapshot());
        return subscribePreferencias(applyFavorites);
    }, [usuarioSessao, routableIds]);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => {
            const next = !prev;
            if (next && typeof window !== 'undefined' && window.innerWidth <= 768) {
                setIsMobileOpen(false);
            }
            return next;
        });
    }, []);

    const expandSidebar = useCallback(() => setIsCollapsed(false), []);
    const collapseSidebar = useCallback(() => setIsCollapsed(true), []);
    const toggleMobileSidebar = useCallback(() => {
        setIsMobileOpen((prev) => {
            const next = !prev;
            if (next && typeof window !== 'undefined' && window.innerWidth <= 768) {
                setIsCollapsed(false);
            }
            return next;
        });
    }, []);
    const openMobileSidebar = useCallback(() => {
        setIsMobileOpen(true);
        if (typeof window !== 'undefined' && window.innerWidth <= 768) {
            setIsCollapsed(false);
        }
    }, []);
    const closeMobileSidebar = useCallback(() => setIsMobileOpen(false), []);

    const isShortcutFavorite = (item: SidebarShortcutItem) => favoriteShortcutIds.includes(item.id);

    const toggleFavoriteShortcut = (item: SidebarShortcutItem) => {
        if (!item.route || !routableIds.includes(item.id)) {
            return;
        }
        const updated = favoriteShortcutIds.includes(item.id)
            ? favoriteShortcutIds.filter((id) => id !== item.id)
            : [...favoriteShortcutIds, item.id];
        setFavoriteShortcutIds(updated);
        void persistFavoriteShortcuts(updated);
    };

    return (
        <SidebarContext.Provider
            value={{
                isCollapsed,
                isMobileOpen,
                menuItems,
                availableShortcuts,
                favoriteShortcutIds,
                toggleCollapse,
                expandSidebar,
                collapseSidebar,
                toggleMobileSidebar,
                openMobileSidebar,
                closeMobileSidebar,
                isShortcutFavorite,
                toggleFavoriteShortcut,
            }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within SidebarProvider');
    }
    return context;
};
